import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPLOYEE_CATEGORIES = [
  'All',
  'Administrative',
  'Woven Line',
  'Knits Line',
  'Cutting Line',
  'Finishing Line'
];

const getCategoryTranslationKey = (category) => {
  const keyMap = {
    'All': 'all',
    'Administrative': 'administrative',
    'Woven Line': 'wovenLine',
    'Knits Line': 'knitsLine',
    'Cutting Line': 'cuttingLine',
    'Finishing Line': 'finishingLine'
  };
  return keyMap[category] || category;
};

export const Payroll = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [snapshots, setSnapshots] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [isCloseMonthOpen, setIsCloseMonthOpen] = useState(false);
  const [blurSalary, setBlurSalary] = useState(() => {
    const saved = localStorage.getItem('payrollBlurSalary');
    return saved === 'true';
  });

  useEffect(() => {
    fetchSnapshots();
  }, []);

  useEffect(() => {
    if (selectedMonth === 'current') {
      fetchCurrentEmployees();
    } else {
      fetchSnapshotData(selectedMonth);
    }
  }, [selectedMonth]);

  const fetchSnapshots = async () => {
    try {
      const response = await axios.get(`${API}/payroll/snapshots`, { headers: getAuthHeader() });
      setSnapshots(response.data);
    } catch (error) {
      console.error('Failed to fetch snapshots');
    }
  };

  const fetchCurrentEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, { headers: getAuthHeader() });
      setEmployees(response.data);
      filterEmployees(response.data, selectedCategory);
    } catch (error) {
      toast.error('Failed to fetch payroll data');
    }
  };

  const fetchSnapshotData = async (snapshotKey) => {
    try {
      const [year, month] = snapshotKey.split('-');
      const response = await axios.get(`${API}/payroll/snapshot/${year}/${month}`, { headers: getAuthHeader() });
      setEmployees(response.data.employees);
      filterEmployees(response.data.employees, selectedCategory);
    } catch (error) {
      toast.error('Failed to fetch snapshot data');
    }
  };

  const handleCloseMonth = async () => {
    try {
      await axios.post(`${API}/payroll/close-month`, {}, { headers: getAuthHeader() });
      toast.success('Month closed successfully! OT, bonuses, deductibles, and withdrawals have been reset.');
      setIsCloseMonthOpen(false);
      fetchSnapshots();
      fetchCurrentEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close month');
    }
  };

  const filterEmployees = (empList, category) => {
    if (category === 'All') {
      setFilteredEmployees(empList);
    } else {
      setFilteredEmployees(empList.filter(emp => emp.category === category));
    }
  };

  useEffect(() => {
    filterEmployees(employees, selectedCategory);
  }, [selectedCategory, employees]);

  const handleBlurToggle = (checked) => {
    setBlurSalary(checked);
    localStorage.setItem('payrollBlurSalary', checked.toString());
  };

  const getMonthLabel = (snapshot) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(snapshot.month) - 1]} ${snapshot.year}`;
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('payroll')}</h2>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[150px]"
            data-testid="month-selector"
          >
            <option value="current">{t('currentMonth')}</option>
            {snapshots.map((snapshot) => (
              <option key={`${snapshot.year}-${snapshot.month}`} value={`${snapshot.year}-${snapshot.month}`}>
                {getMonthLabel(snapshot)}
              </option>
            ))}
          </select>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            data-testid="payroll-category-filter"
          >
            {EMPLOYEE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{t(getCategoryTranslationKey(cat))}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={blurSalary}
              onChange={(e) => handleBlurToggle(e.target.checked)}
              className="w-4 h-4"
              data-testid="blur-salary-toggle"
            />
            <span className="text-sm" lang={language === 'ar' ? 'ar' : 'en'}>{t('blurSalaryNetPay')}</span>
          </label>
          {user?.role === 'admin' && selectedMonth === 'current' && (
            <Dialog open={isCloseMonthOpen} onOpenChange={setIsCloseMonthOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-destructive hover:bg-destructive/90" data-testid="close-month-btn">
                  <Lock className="w-4 h-4" />
                  <span>{t('closeMonth')}</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('closeCurrentMonth')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('closeMonthWarning')}
                  </p>
                  <p className="text-sm font-semibold text-destructive">
                    {t('closeMonthConfirm')}
                  </p>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setIsCloseMonthOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button className="bg-destructive hover:bg-destructive/90" onClick={handleCloseMonth}>
                      {t('yesCloseMonth')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {selectedMonth !== 'current' && (
        <div className="mb-4 p-3 bg-muted/50 rounded-md border border-border">
          <p className="text-sm text-muted-foreground">
            📊 {t('viewingArchived')} <span className="font-semibold">{getMonthLabel(snapshots.find(s => `${s.year}-${s.month}` === selectedMonth) || {month: selectedMonth.split('-')[1], year: selectedMonth.split('-')[0]})}</span>
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('number')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('salary')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('ss')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('ot')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('bonuses')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('deductibles')}</th>
              <th className="border border-border p-3 text-end font-semibold bg-accent/10" lang={language === 'ar' ? 'ar' : 'en'}>{t('netPay')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('withdrawals')}</th>
              <th className="border border-border p-3 text-end font-semibold bg-success/10" lang={language === 'ar' ? 'ar' : 'en'}>{t('balanceRemaining')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((emp, idx) => {
              const netPay = emp.salary - emp.ss + emp.ot + emp.bonuses - emp.deductibles;
              const balanceRemaining = netPay - (emp.withdrawals || 0);
              return (
                <tr key={idx} className="hover:bg-muted/30" data-testid={`payroll-row-${idx}`}>
                  <td className="border border-border p-3 font-mono-numeric">{emp.employee_number}</td>
                  <td className="border border-border p-3">{emp.name}</td>
                  <td className={`border border-border p-3 text-end font-mono-numeric ${blurSalary ? 'blur-sm select-none' : ''}`}>{emp.salary.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-red-600">-{emp.ss.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-green-600">+{emp.ot.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-green-600">+{emp.bonuses.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-red-600">-{emp.deductibles.toFixed(2)}</td>
                  <td className={`border border-border p-3 text-end font-mono-numeric font-semibold bg-accent/10 ${blurSalary ? 'blur-sm select-none' : ''}`}>{netPay.toFixed(2)} {t('currency')}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-red-600">-{(emp.withdrawals || 0).toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric font-bold bg-success/10">{balanceRemaining.toFixed(2)} {t('currency')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredEmployees.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-payroll">
            <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noPayrollData')}</p>
          </div>
        )}
      </div>

      {filteredEmployees.length > 0 && (
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="p-4 bg-accent/10 rounded-md">
            <div className="flex justify-between items-center">
              <span className="font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('total')} {t('netPay')}:</span>
              <span className="text-2xl font-bold font-mono-numeric">
                {filteredEmployees.reduce((sum, emp) => sum + (emp.salary - emp.ss + emp.ot + emp.bonuses - emp.deductibles), 0).toFixed(2)} {t('currency')}
              </span>
            </div>
          </div>
          <div className="p-4 bg-success/10 rounded-md">
            <div className="flex justify-between items-center">
              <span className="font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('total')} {t('balanceRemaining')}:</span>
              <span className="text-2xl font-bold font-mono-numeric">
                {filteredEmployees.reduce((sum, emp) => sum + (emp.salary - emp.ss + emp.ot + emp.bonuses - emp.deductibles - (emp.withdrawals || 0)), 0).toFixed(2)} {t('currency')}
              </span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};